import {
  AREA_LANDING_PATHS,
  isViewOnlyNoticeExempt,
} from "@/features/internal-user/area-routes";
import { CrmArea } from "@/features/internal-user/services/access";
import { Eye } from "lucide-react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import ThreeDotsLoader from "./three-dots-loader";
import { usePermissionContext } from "./permission-context";

/**
 * The first page in sidebar order this role can actually open, or null if there
 * is none. Used for every redirect, so nobody is ever sent to a page that will
 * bounce them straight back.
 */
function useFirstAllowedPath(): string | null {
  const { areas, isSuper } = usePermissionContext();
  if (isSuper) return AREA_LANDING_PATHS[0].path;
  const first = AREA_LANDING_PATHS.find(({ area }) => areas[area]?.view);
  return first?.path ?? null;
}

function NoAreaAccess() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center gap-2 px-6 text-center">
      <h1 className="text-lg font-medium">No pages are available to you</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        Your role has not been given View access to any area of the CRM. Ask an
        administrator to grant access on the Manage Access screen.
      </p>
    </div>
  );
}

/**
 * What the signed-in person may do on a given area: change things, look at them,
 * or neither.
 *
 * Read from the same grid the server enforces, resolved for THIS Clerk identity's
 * role — so two people looking at the same page can legitimately see different
 * answers, and each is told which one applies to them.
 */
export function useAreaMode(area?: CrmArea): "edit" | "view" | "none" {
  const { areas, isSuper } = usePermissionContext();
  // A super role has everything, and a page outside the grid is governed by
  // permissions alone — neither is view-only by virtue of an area.
  if (isSuper || !area) return "edit";
  const grant = areas[area];
  if (!grant?.view) return "none";
  return grant.edit ? "edit" : "view";
}

/**
 * Says plainly, on every page, when the role can look but not touch.
 *
 * Without it view-only reads as a broken page: the controls are simply absent,
 * with nothing saying why, and the natural conclusion is that the CRM has lost
 * its buttons rather than that access was deliberately scoped. Shown only in the
 * view-only case — "you can edit" needs no banner, and no-access never renders.
 */
function ViewOnlyNotice({ area }: { area: CrmArea }) {
  const mode = useAreaMode(area);
  const { pathname } = useLocation();
  // A link-building page has no editing controls to hide, so no banner.
  if (mode !== "view" || isViewOnlyNoticeExempt(pathname)) return null;

  return (
    <div className="mx-4 mb-3 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      <Eye className="h-3.5 w-3.5 shrink-0" />
      <span>
        <span className="font-medium">View only.</span> Your role can open this
        page but not change anything on it. Editing controls are hidden, and the
        server refuses the changes regardless.
      </span>
    </div>
  );
}

/**
 * Route-level gate: renders the page only if the role has View on its area.
 *
 * The server refuses the API calls regardless (RolesGuard checks the same grid),
 * so this is not the security boundary — it is what stops a page rendering its
 * shell and then filling with 403 errors, which reads as a broken CRM rather
 * than as access the operator does not have.
 */
export function AreaRoute({ area }: { area: CrmArea }) {
  const { areas, isSuper, isLoaded } = usePermissionContext();
  const fallback = useFirstAllowedPath();

  // Fails closed while loading, matching <HasAccess>. Rendering the page first
  // and pulling it away is worse than a moment of loader.
  if (!isLoaded) return <ThreeDotsLoader />;

  if (isSuper || areas[area]?.view) {
    return (
      <>
        <ViewOnlyNotice area={area} />
        <Outlet />
      </>
    );
  }

  return fallback ? <Navigate to={fallback} replace /> : <NoAreaAccess />;
}

/**
 * Where "/" and any unmatched path land. Replaces the old hardcoded redirect to
 * "users", which loops for a role that cannot view Users.
 */
export function AreaLanding() {
  const { isLoaded } = usePermissionContext();
  const fallback = useFirstAllowedPath();

  if (!isLoaded) return <ThreeDotsLoader />;

  return fallback ? <Navigate to={fallback} replace /> : <NoAreaAccess />;
}
